import { isPlatformBrowser } from '@angular/common';
import { ElementRef, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { BevelFilter, DropShadowFilter } from 'pixi-filters';
import { Viewport, IViewportOptions, ISnapZoomOptions } from 'pixi-viewport';
import {
  Application,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Texture,
  BlurFilter,
  Assets,
  FederatedPointerEvent,
  Spritesheet,
  Renderer,
} from 'pixi.js';
import { BehaviorSubject, combineLatest, ReplaySubject, Subject } from 'rxjs';
import { filter, map, pairwise, startWith, take, takeUntil, throttleTime } from 'rxjs/operators';

import { AppComponent } from 'app/app.component';
import { LaunchDarklyFlags, PixiInspector } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { AppState, Timeout } from 'app/types';
import assertNever from 'app/utils/assert-never';
import { getResizeUri } from 'app/utils/image';
import { Loggable } from 'app/utils/logging/loggable';
import { notNull, notNil, isNil } from 'app/utils/stream-util';
import { notUndefined } from 'app/utils/stream-util';
import { sprites } from 'assets/pixi/sprites';
import Social from 'entity/Social';
import SocialThemeImageTile from 'entity/SocialThemeImageTile';
import User from 'entity/User';
import {
  ClusterCenter,
  ParticipantMovement,
  ParticipantPosition,
  InitialPositionMessage,
  IncomingMessage,
  IncomingMessageType,
  TeleportMessage,
  OutgoingMessageType,
  MediaMessage,
  MediaType,
  MovedMessage,
} from 'messages/websocket.messages';
import { ZoomController, ZoomDirection, ZoomOptions } from './zoom-controller';

import {
  AddArtifact,
  ArtifactOperation,
  ArtifactOperationType,
  ArtifactService,
  RemoveArtifact,
} from '../services/artifact/artifact.service';
import { BrowserService } from '../services/browser.service';

import { AddCTA, CTAOperation, CTAOperationType, CTAService, RemoveCTA } from '../services/cta/cta.service';
import { DeviceService } from '../services/device/device.service';
import { SceneService } from '../services/scene.service';
import { WebsocketService } from '../services/websocket.service';
import { SocialMiniMapComponent } from '../social-mini-map/social-mini-map.component';
import SoundControl from '../sound-control';
import { actions, SpecialAccessGroupModalMode } from '../state/app';
import { socialAppSelectors } from '../state/app/';
import { getGuestRoomPosition } from '../state/app/selectors';
import { mediaSelectors, SimpleMediaDeviceInfo } from '../state/media';
import { mapRoomSizeToScale, RoomSizeType } from '../types/room';

import BackgroundSprite from './background-sprite';
import { ZIndex } from './constants';
import CTARoomItem from './cta/cta-item';
import { GroupManager } from './group-manager';
import Joystick from './joystick';
import MouseMovementController from './mouse-movement-controller';
import { ParallaxLayer } from './parallax-layer';
import { Player } from './player';
import { ConnectionStatus } from './player-components/player.state';
import { CursorTypes } from './scene-manager.types';
import SocialArtifactSprite from './social-artifact-sprite';
import { IScene } from './types';
import { MatSnackBar } from '@angular/material/snack-bar';
import { initDevtools } from '@pixi/devtools';
import { PlayerVisibilityService } from './player-visibility.service';

const MIN_ANDROID_ZOOM = 0.5;
const MIN_DESKTOP_ZOOM = 0.8;
// const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.4;
const ZOOM_OFFSET = 0.1;
const MOVEMENT_INTERVAL = 100;
// apply when not in group
export const DEFAULT_ZOOM = 0.6;

type ParticipantLocation = Omit<ParticipantMovement, 'u'>;

type SagQueueType = Map<number, User[]>;

export class SceneManager extends Loggable implements IScene {
  public app?: Application;
  public participants: Map<string, Player> = new Map<string, Player>();
  public player?: Player;
  public viewport!: Viewport;
  //Container for player, room items, navigation items
  public roomContainer = new Container();
  public bounds!: Rectangle;
  public joystick?: Joystick;
  public numParticipants = 0; // This should increase as new people joined and decrease as they leave - might want to rely on _sceneObjects count instead.
  public maxParticipantsJoined = 0;
  public groupManager: GroupManager | undefined;

  private preParticipantMap: Record<string, ParticipantLocation> = {};
  public social?: Social;
  public w = 4000;
  public h = 4000;
  public participantsCountChange: Subject<number> = new Subject<number>();
  public participantAdded$ = new ReplaySubject<Player>(Infinity);
  public participantRemoved$ = new ReplaySubject<string>(Infinity);
  public fullClusters: number[] = [];

  public customCursorVisible = false;
  private playerMap: Record<string, Player> = {};
  private interval?: Timeout;
  private centers: ClusterCenter[] = [];
  private artifacts: SocialArtifactSprite[] = [];
  private navItems: Sprite[] = [];
  private border?: Container;
  private customCursor!: Sprite;
  private lastMousePos?: Partial<Rectangle>;
  private ctaItems: CTARoomItem[] = [];

  private near?: ParallaxLayer;
  private mid?: ParallaxLayer;
  private far?: ParallaxLayer;
  private backgroundSprites?: (BackgroundSprite | ParallaxLayer)[];

  private destroyed$ = new ReplaySubject<boolean>(1);
  private isVideoEnabled = true;
  private enableNavItems = false;

  // Customer API settings
  private isGuestZoomLevelApplied = false;
  private guestZoomLevel = DEFAULT_ZOOM;
  public autoZoomEnabled = false;
  public autoZoomEnabled$ = new BehaviorSubject<boolean>(false);
  private guestRoomPosition: { x: number; y: number } | undefined;
  public enableCenterViewport: boolean | undefined;

  private mouseMovementController!: MouseMovementController;
  public isMobile = this.browserService?.isMobileOrTablet ?? false;
  private playerCulling = false;
  private isLoading = true;
  public zoomController!: ZoomController;

  // Add a cleanupIntervalId property to avoid using 'as any'
  private cleanupIntervalId: NodeJS.Timeout | null = null;

  public get currentZoom() {
    return this.zoomController?.currentZoom ?? DEFAULT_ZOOM;
  }

  public readonly maxZoom = MAX_ZOOM;
  public snapZoomOptions: ExtendedISnapZoomOptions = {
    removeOnComplete: true,
    removeOnInterrupt: false,
    time: 1000,
    ease: 'easeInOutSine',
    // added to zoom when in group with autozoom
    ratio: 1.4,
    delay: 0,
  };

  // Forward the zoom direction subject from the controller for backwards compatibility
  public get zoomDirection$() {
    return this.zoomController?.zoomDirection$ ?? new Subject<ZoomDirection>();
  }
  private snapZoomTimeout: string | number | Timeout | undefined;
  private fitTilesToRoom = false;
  private soundControl = SoundControl.getInstance(this.sceneService);
  public sprites!: Spritesheet;
  private ktx2Compression = false;
  private borderClamp = false;
  private healthCheckFlag = false;

  private enableAudioRange = false;
  public sagQueue$ = new BehaviorSubject<SagQueueType>(new Map<number, User[]>());
  private sagQueue: SagQueueType = new Map<number, User[]>();
  private sagClusters: Map<number, { x: number; y: number; sag: boolean }> = new Map<
    number,
    { x: number; y: number; sag: boolean }
  >();

  private specialAccessGroupsFF = false;
  private assetsFailTicker = 0;

  private async loadCriticalAssets(): Promise<void> {
    try {
      const texture: Texture = await Assets.load({
        src: sprites.meta.image,
        data: {
          autoGenerateMipmaps: true,
        },
      });
      this.sprites = new Spritesheet(texture, sprites);
      await this.sprites.parse();
      // Load other critical assets here if needed
    } catch (error) {
      this.error('Failed to load critical assets', error);
      this.assetsFailTicker += 1;
      await delayFor(1000);
      // Retry loading critical assets
      if (this.assetsFailTicker < 5) {
        return this.loadCriticalAssets();
      }
      alert('Something unexpected has happened. Please refresh the page.');
    }
  }

  public constructor(
    public elementRef: ElementRef,
    private sceneService: SceneService,
    private deviceService: DeviceService,
    private artifactService: ArtifactService,
    private websocketService: WebsocketService,
    private browserService: BrowserService,
    private ctaService: CTAService,
    private store: Store<AppState>,
    private playerVisibilityService: PlayerVisibilityService,
    private _snackBar?: MatSnackBar,
    private ngZone?: NgZone,
  ) {
    super();
    this.setDisplayName('SceneManager');

    this.roomContainer.name = 'room-container';
    this.roomContainer.sortableChildren = true;

    const appHTMLElement = this.elementRef.nativeElement as HTMLElement;

    void (async () => {
      try {
        await this.loadCriticalAssets();
      } catch (error) {
        this.error('Failed to load critical assets, application initialization aborted', error);
        return;
      }

      this.app = new Application({
        resizeTo: appHTMLElement,
        backgroundAlpha: 0,
        autoDensity: true,
        // this comes out to 1 on desktop
        resolution: window.devicePixelRatio,
        autoStart: true,
        // does anything?
        powerPreference: 'high-performance',
        eventMode: 'passive',
        antialias: false,
        eventFeatures: {
          move: true,
          /** disables the global move events which can be very expensive in large scenes */
          globalMove: false,
          click: true,
          wheel: true,
        },
      });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.pixi_inspector)
        .pipe(
          take(1),
          filter((pixi_inspector) => pixi_inspector === PixiInspector.MAIN),
        )
        .subscribe(() => {
          void initDevtools({
            app: this.app,
          });
        });

      this.app!.stage.eventMode = 'static';

      this.elementRef.nativeElement.appendChild(this.app!.view);
      const viewportOptions: IViewportOptions = {
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        worldWidth: this.w,
        worldHeight: this.h,
        // events: this.app.renderer.events,
      };
      this.viewport = new Viewport(viewportOptions);

      // this.viewport.sortableChildren = true;
      this.viewport.addChild(this.roomContainer);
      this.bounds = this.viewport.getVisibleBounds();

      this.app!.stage.addChild(this.viewport);
      this.customCursor = new Sprite();
      this.customCursor.interactiveChildren = false;
      this.customCursor.name = 'custom-cursor';
      this.mouseMovementController = MouseMovementController.getInstance(this);
      this.configureCustomCursor();

      // Initialize the zoom controller
      const zoomOptions: ZoomOptions = {
        minZoom: MIN_DESKTOP_ZOOM,
        maxZoom: this.maxZoom,
        defaultZoom: DEFAULT_ZOOM,
        zoomStep: ZOOM_OFFSET,
        animationTime: this.snapZoomOptions.time,
        animationEase: this.snapZoomOptions.ease as string,
      };
      this.zoomController = new ZoomController(this.viewport, zoomOptions);

      // Subscribe to zoom complete events to check bounds and update UI
      this.zoomController.zoomComplete$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        // Notify zoom-pixi-world-controls that zoom bounds may have changed
        this.zoomDirection$.next({ value: 'none' });
      });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.special_access_groups)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((specialAccessGroups) => {
          this.specialAccessGroupsFF = specialAccessGroups;
        });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_center_viewport)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((enableCenterViewport) => (this.enableCenterViewport = enableCenterViewport));

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.fit_tiles_to_room)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((fitTilesToRoom) => (this.fitTilesToRoom = fitTilesToRoom));

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.player_culling)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((playerCulling) => (this.playerCulling = playerCulling));

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.health_check)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((healthCheck) => (this.healthCheckFlag = healthCheck));

      combineLatest([
        getFeatureFlagObservable(this.store, LaunchDarklyFlags.border_clamp),
        this.store.select(socialAppSelectors.getCurrentSocial),
      ])
        .pipe(takeUntil(this.destroyed$))
        .subscribe(([borderClamp, social]) => {
          // clamping the border is only enabled when the room is not a mini room
          this.borderClamp = borderClamp && social?.startingWidth !== RoomSizeType.MINI;
        });

      // getFeatureFlagObservable(this.store, LaunchDarklyFlags.ktx2_compression)
      //   .pipe(takeUntil(this.destroyed$))
      //   .subscribe((ktx2Compression) => (this.ktx2Compression = ktx2Compression));

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.pinch_zoom)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((pinchZoomEnabled) => {
          if (pinchZoomEnabled && this.isMobile) {
            this.viewport
              ?.clampZoom({
                minWidth: 1000,
                minHeight: 1000,
                maxWidth: 5000,
                maxHeight: 5000,
              })
              .pinch({
                noDrag: true,
                percent: 1,
              })
              .decelerate();

            this.viewport.on('pinch-start', this.handlePinch.bind(this));
          }
        });

      // Remove the theme background when the user leaves the room
      this.store
        .select(socialAppSelectors.getCurrentSocial)
        .pipe(
          pairwise(),
          filter(([previousSocial, social]) => isNil(social) && notNil(previousSocial)),
          takeUntil(this.destroyed$),
        )
        .subscribe(() => {
          this.removeThemeBackground();
        });

      this.store
        .select(socialAppSelectors.getCurrentSocial)
        .pipe(
          startWith(undefined),
          pairwise(),
          filter(([previousSocial, social]) => isNil(previousSocial) && notNil(social)),
          map(([, social]) => social),
          filter(notNil),
          takeUntil(this.destroyed$),
        )
        .subscribe((social) => {
          void this.addThemeBackground(social);
        });

      // Switch the theme background when the user changes rooms
      this.store
        .select(socialAppSelectors.getCurrentSocial)
        .pipe(
          pairwise(),
          filter(
            ([previousSocial, social]) =>
              notNil(previousSocial) && notNil(social) && previousSocial.themeId !== social.themeId,
          ),
          takeUntil(this.destroyed$),
        )
        .subscribe(([, social]) => {
          this.removeThemeBackground();
          this.social = social;
          if (social) {
            void this.addThemeBackground(social);
          }
        });

      if (this.browserService?.isAndroidDevice()) {
        this.zoomController.options.minZoom = MIN_ANDROID_ZOOM;
      }

      this.websocketService?.message$.subscribe(this.handleWebsocketMessage.bind(this));

      this.deviceService?.cameraStream$
        .pipe(
          filter((ms): ms is MediaStream => {
            return !!ms && ms.active && ms.getVideoTracks().length > 0 && ms.getVideoTracks()[0].enabled;
          }),
          // TODO: this should not be nessasary, figure out why this is firing repeatedly
          throttleTime(100),
          takeUntil(this.destroyed$),
        )
        .subscribe((stream: MediaStream) => {
          if (this.ngZone) {
            this.ngZone.runOutsideAngular(() => {
              void this.updateCameraStream(stream);
            });
          }
        });

      this.deviceService?.echoAudioStream$
        .pipe(filter(notNil), takeUntil(this.destroyed$))
        .subscribe((stream) =>
          this.updateEchoAudioStream(stream).catch((e) => this.error('Failed to update echo stream', e)),
        );

      this.store
        .select(mediaSelectors.getSelectedSpeaker)
        .pipe(filter(notNull), takeUntil(this.destroyed$))
        .subscribe((speaker) => this.updateSpeaker(speaker));

      this.store
        .select(socialAppSelectors.getFullClusters)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((fullClusters) => (this.fullClusters = fullClusters));

      this.store
        .select(mediaSelectors.isVideoEnabled)
        .pipe(filter(notNull), takeUntil(this.destroyed$))
        .subscribe((enabled) => (this.isVideoEnabled = enabled));

      // Customer API Settings
      this.store
        .select(socialAppSelectors.isGuestZoomLevelApplied)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((state) => {
          this.isGuestZoomLevelApplied = state;
        });

      this.store
        .select(socialAppSelectors.getGuestZoomLevel)
        .pipe(filter(notUndefined), takeUntil(this.destroyed$))
        .subscribe((setting) => {
          this.guestZoomLevel = setting;
        });

      this.store
        .select(socialAppSelectors.isInSocial)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isInSocial) => {
          if (isInSocial) {
            if (this.app && this.app.view && this.app.view.style) {
              this.app.view.style.cursor = 'none';
            }
            this.handlePointerOver();
          } else {
            this.handlePointerOut();
          }
        });

      this.store
        .select(getGuestRoomPosition)
        .pipe(filter(notNil), takeUntil(this.destroyed$))
        .subscribe((guestRoomPosition) => {
          this.guestRoomPosition = guestRoomPosition;
        });

      this.sagQueue$.pipe(filter(notUndefined), takeUntil(this.destroyed$)).subscribe((sagQueue) => {
        this.sagQueue = sagQueue;
      });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_audio_range)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((enableAudioRange) => (this.enableAudioRange = enableAudioRange));

      // Handle room items
      this.artifactService?.artifactsOperation$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((op) => this.updateArtifacts(op));
      this.ctaService?.ctaOperation$.pipe(takeUntil(this.destroyed$)).subscribe((op) => {
        this.updateCTAItems(op);
      });

      this.app!.ticker.add(this.runPixiMainLoop, this);
      this.joystick = new Joystick(this.elementRef, MOVEMENT_INTERVAL, this);
      this.mouseMovementController.initMovementListener({
        intervalTimeFrame: MOVEMENT_INTERVAL,
        isMobile: this.browserService ? this.browserService?.isMobileOrTablet : false,
        scene: this,
      });

      // TODO: this is a kludge to prevent health check from running before players are created and double tapping, but introduces potential race condition, refactor
      await delayFor(1000);
      this.isLoading = false;
    })();
  }

  public handlePinch() {
    this.toggleAutoZoom(true);
  }

  public runIfPixiInitialized(callback: () => void): void {
    if (this.app!.stage) {
      callback();
    } else {
      this.app!.ticker.addOnce(() => {
        callback();
      });
    }
  }

  public get fps(): number {
    return this.app!.ticker.FPS;
  }

  public get zoom(): number {
    return this.currentZoom;
  }

  public async handleInitPosMessage(initPosMsg: InitialPositionMessage) {
    // Set viewport height/width in initMessage
    // Allows minimap to create and place participant in correct position
    this.viewport.worldWidth = initPosMsg.worldWidth;
    this.viewport.worldHeight = initPosMsg.worldHeight;

    await this.setInitialPosition(initPosMsg);
    this.setPositions(initPosMsg.positions, true);
    this.resizeWorld(initPosMsg.worldWidth, initPosMsg.worldHeight);
    await this.setInitialMainGroupCenter(initPosMsg);
  }

  /**
   * Sets the initial center of the main group.
   *
   * @param cluster - The initial coordinates of the main cluster
   */
  public async setInitialMainGroupCenter({
    cluster: clusterId,
    clusterX: x,
    clusterY: y,
    sag,
  }: Pick<InitialPositionMessage, 'clusterX' | 'clusterY' | 'cluster' | 'sag'>): Promise<void> {
    const player = await this.getPlayerAsync();
    const limitExceeded = player.groupWarning;
    const center = { x, y };
    this.groupManager?.setGroup({
      clusterId,
      player,
      center,
      limitExceeded,
      sag,
    });
  }

  public isInZone(caller: string) {
    let lastLoggedTime = 0;
    const logInterval = 1000;
    this.app!.ticker.add(() => {
      const currentTime = Date.now();
      if (currentTime - lastLoggedTime >= logInterval) {
        this.error(`Inside Angular Zone: ${caller} ${NgZone.isInAngularZone()}`);
        lastLoggedTime = currentTime;
      }
    });
  }

  // dev utillity, call wiith `SceneManager.isInZoneNow('player.ts setVideoElement');`
  // to check if you are in the angular zone
  public static isInZoneNow(caller: string) {
    // eslint-disable-next-line no-console
    console.error(`Inside Angular Zone: ${caller} ${NgZone.isInAngularZone()}`);
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t } = msg;
    switch (t) {
      case IncomingMessageType.MOVED: {
        const { p } = msg;

        this.setPositions(p);
        if (this.groupManager) {
          this.setGroupCenters(msg.c);
        }

        if (this.specialAccessGroupsFF) {
          void this.handleSpecialAccessGroup(msg);

          if (this.sceneService.showSagDebuggerStats) {
            this.handleSagDebuggerData(msg);
          }
        }
        return;
      }

      case IncomingMessageType.RESIZE: {
        this.resizeWorld(msg.width, msg.height);
        return;
      }

      case IncomingMessageType.SESSION_CREATED: {
        this.websocketService.sendMessage({
          t: OutgoingMessageType.GET_MOVEMENT_END_DURATION,
        });

        if (!this.guestRoomPosition) {
          return;
        }
        // When we have a session, the user is in the room and can teleport
        this.info('Guest position is set, teleporting user', this.guestRoomPosition);
        const teleported: TeleportMessage = {
          t: OutgoingMessageType.TELEPORT,
          ...this.guestRoomPosition,
        };
        this.websocketService.sendMessage(teleported);
        return;
      }

      case IncomingMessageType.INITPOS: {
        // If we have a player, we have already received the initpos message
        if (this.player) {
          return;
        }
        this.handleInitPosMessage(msg).catch((e) => {
          this.error('Failed to handle initpos message', e);
        });
        return;
      }

      case IncomingMessageType.MOVEMENT_END_DURATION: {
        const { duration } = msg;
        if (this.player) {
          this.player.isMovingTimeoutDuration = duration;
        }
        return;
      }

      case IncomingMessageType.MEDIA:
        this.handleMediaMessage(msg);
        break;
    }
  };

  private handleMediaMessage(msg: MediaMessage): void {
    const mediaMessage = msg as MediaMessage;
    const { mediaType, receiving, userId } = mediaMessage;
    if (receiving && mediaType === MediaType.VIDEO) {
      this.setPlayerConnectionStatus(userId, ConnectionStatus.NONE);
    }
  }

  private updateArtifacts = async (op: ArtifactOperation) => {
    const { type } = op;
    switch (type) {
      case ArtifactOperationType.ADD:
        {
          const { artifact } = op as AddArtifact;
          await Assets.load(this.compressed(artifact.imageURI ?? ''));
          const sprite = new SocialArtifactSprite(artifact);
          sprite.name = `artifact-${artifact.artifactId}`;
          sprite.anchor.set(0.5);
          sprite.transform.scale.set(1, 1);
          sprite.visible = true;
          sprite.zIndex = ZIndex.Artifact;

          this.artifacts.push(sprite);
          this.roomContainer.addChild(sprite);
        }
        break;

      case ArtifactOperationType.REMOVE:
        {
          const { artifact } = op as RemoveArtifact;
          // TODO: Remove the artifact from the viewport
          this.warn(`Removing artifact ${artifact.artifactId}, removal is not implemented`);
        }
        break;

      case ArtifactOperationType.CLEAR:
        this.destroyArtifacts();
        break;

      default:
        assertNever(type);
    }
  };

  private updateCTAItems = (op: CTAOperation) => {
    const { type } = op;
    switch (type) {
      case CTAOperationType.ADD:
        {
          const { cta } = op as AddCTA;
          const renderer = this.sceneService.sceneManager?.app?.renderer;
          if (!renderer) {
            this.error('Renderer is undefined');
            return;
          }
          const item = new CTARoomItem(renderer as Renderer, {
            item: cta,
            ratio: 1,
            pointerdown: () => {
              this.ctaService.handleButtonClick(cta);
            },
            mouseover: () => {
              this.hideCustomCursor();
            },
            mouseout: () => {
              this.reRenderCustomCursor();
              this.showCustomCursor();
            },
            compressed: this.ktx2Compression,
          });
          this.roomContainer.addChild(item);

          this.ctaItems.push(item);
        }
        break;

      case CTAOperationType.REMOVE:
        {
          const { cta } = op as RemoveCTA;
          this.warn(`Removing cta ${cta.CTAItemId}, removal is not implemented`);
        }
        break;

      case CTAOperationType.CLEAR:
        this.destroyCTAItems();
        break;

      default:
        assertNever(type);
    }
  };

  // Handle Customer API Settings
  public handleCustomerAPISettings(): void {
    if (this.isGuestZoomLevelApplied) {
      // Apply the guest zoom level using the ZoomController
      void this.zoomController.zoomTo(this.guestZoomLevel);
    }
  }

  private handlePointerOver() {
    this.app!.stage.cursor = this.customCursorVisible ? CursorTypes.CUSTOM_CURSOR : CursorTypes.PREDETERMINED;
    this.customCursor.visible = this.customCursorVisible;
  }

  private handlePointerOut() {
    if (this.customCursor) {
      this.customCursor.visible = false;
    }
  }

  /**
   * Configure a custom cursor and listens for pointer-events to hide/show the custom cursor in zones that are not open space.
   */
  private configureCustomCursor(): void {
    if (!this.app) {
      this.error('configureCustomCursor: App is not initialized');
      return;
    }
    this.mouseMovementController.viewport = this.viewport;

    this.customCursor.zIndex = ZIndex.Cursor;
    this.roomContainer.addChild(this.customCursor);
    this.customCursor.cursor = 'none';
    if (this.app!.renderer.events?.cursorStyles) {
      this.app!.renderer.events.cursorStyles.default = async () => {
        this.customCursor.texture = await Assets.load('/assets/pixi/custom-arrow.png');
      };
    }

    this.mouseMovementController?.viewport
      .on('pointerover', this.handlePointerOver, this)
      .on('pointerout', this.handlePointerOut, this)
      .on('pointermove', this.handlePointerMove)
      .on('pointerdown', this.handlePointerDown)
      // Movement end listeners
      .on('pointerup', () => this.onMouseLeave)
      .on('mouseup', (e: FederatedPointerEvent) => this.onPointerUp(e));
    this.app!.stage.cursor = CursorTypes.CUSTOM_CURSOR;
    this.customCursor.visible = true;

    this.mouseMovementController.movementEnabled = this.browserService ? !this.browserService?.isMobileOrTablet : true;

    const mainPlayer = this.getPlayer();
    if (mainPlayer?.cursorIndicator) {
      mainPlayer.cursorIndicator.visible = true;
    }

    document.documentElement.addEventListener('onmouseleave', this.mouseMovementController.onMouseLeave);
  }

  private onPointerUp(e: FederatedPointerEvent) {
    this.mouseMovementController.onPointerUp(e);
  }

  private onMouseLeave() {
    this.mouseMovementController.onMouseLeave();
  }

  private handlePointerMove = (event: FederatedPointerEvent) => {
    const { x, y } = event.global;
    this.changeCursorAngle({ x, y });
    this.mouseMovementController.onPointerMove(event);
  };

  private handlePointerDown = (event: FederatedPointerEvent) => {
    if (!this.customCursor.visible) {
      return;
    }
    this.mouseMovementController.onPointerDown(event);
  };

  private setCustomCursorVisibility(visible: boolean): void {
    this.customCursorVisible = visible;
  }

  public async playerInView({ player, visible }: { player: Player; visible: boolean }): Promise<void> {
    await this.sceneService.playerInView({ player, visible });
  }

  public movePlayers(): void {
    if (this.player) {
      this.runIfPixiInitialized(() => this.player?.move(this.bounds));
      // We also need to recalculate cursor angle when moving otherwise cursor angle won't be
      // recalculated until the user moves the cursor again.
      this.changeCursorAngle();

      for (const p of this.participants.values()) {
        p.move(this.bounds);
      }
    }
  }

  private resizeWorld(width: number, height: number): void {
    if (!this.viewport) {
      return;
    }
    if (width && height) {
      this.info(`Did resize world to width: ${width} and height: ${height}`);
    }

    this.w = width;
    this.h = height;
    this.viewport.worldWidth = width;
    this.viewport.worldHeight = height;
    this.setWorldBorder(width, height);
  }

  public setWorldBorder(width: number, height: number): void {
    if (!isPlatformBrowser(AppComponent.PLATFORM_ID)) {
      return;
    }

    if (this.border) {
      this.roomContainer.removeChild(this.border);
    }
    this.border?.destroy({ children: true });
    this.border = undefined;

    const padding = 25;
    const w = width + padding;
    const h = height + padding;

    const shadeContainer = new Container();
    shadeContainer.name = 'border-shade-container';
    const shade = new Graphics()
      .beginFill(0x000000, 0.5)
      .drawRect(-w / 2, -h / 2, w + 240, h + 240)
      .endFill();
    shade.name = 'border-shade';
    shade.position.set(-120, -120);
    const mask = new Graphics()
      .beginFill(0xffffff)
      .drawRoundedRect(-(w / 2), -(h / 2), w, h, 25)
      .endFill();

    // shadeContainer.mask = mask;
    shadeContainer.addChild(mask, shade);

    this.border = new Container();
    this.border.zIndex = ZIndex.Border;
    this.border.name = 'border';
    this.border.eventMode = 'none';

    const line = new Graphics()
      .beginFill(0xffffff, 0.0)
      .lineStyle(50, 0xffffff, 1)
      .drawRoundedRect(-(w / 2), -(h / 2), w, h, 25)
      .endFill();
    line.name = 'border-line';
    line.filters = [
      new DropShadowFilter({
        color: 0x000000,
        alpha: 0.5,
        blur: 8,
        quality: 5,
        offset: { x: 0, y: 25 },
      }),
      new BevelFilter({
        lightAlpha: 0.5,
        shadowAlpha: 0.5,
      }),
    ];

    const shadow = new Graphics()
      .beginFill(0x000000, 0.0)
      .lineStyle(50, 0x000000, 0.3)
      .drawRoundedRect(-(w / 2), -(h / 2) + 25, w, h, 25)
      .endFill();
    shadow.name = 'border-shadow';
    shadow.filters = [new BlurFilter(5)];

    this.border.addChild(shadow, line);
    this.roomContainer.addChild(
      // NOTE: no inverse mask in v7
      // shadeContainer,
      this.border,
    );
  }

  private async addThemeBackground(social: Social): Promise<void> {
    this.info('Adding theme background');
    const textureWidth = 4000;
    const textureHeight = 4000;
    this.social = social;

    this.zoomController.options.minZoom = mapRoomSizeToScale(this.social.startingWidth ?? 0) ?? MIN_DESKTOP_ZOOM;

    if (!this.viewport) {
      return;
    }
    if (!social?.theme) {
      return;
    } // Prevent issues on reload/close

    if (this.borderClamp && this.social && this.social.startingWidth && this.social.startingHeight && !this.isMobile) {
      const clampBorder = this.social.startingWidth / 2 + 60 * 2;
      this.viewport.clamp({
        left: -clampBorder,
        right: clampBorder,
        top: -clampBorder,
        bottom: clampBorder,
      });
    }

    const { imageFarURI, imageMidURI, imageNearURI, tiles = [] } = social.theme;
    const layers: BackgroundSprite[] = [];
    const parallaxLayers: ParallaxLayer[] = [];

    // Set continuous client background
    //  Otherwise set far, near, mid background
    if (this.isThemeContinuousBackground && !this.fitTilesToRoom) {
      await Assets.load(
        tiles.map((tile) => {
          return {
            src: this.compressed(tile.uri ?? ''),
            data: {
              autoGenerateMipmaps: true,
            },
          };
        }),
      );
      for (const tile of tiles) {
        const image = Texture.from(this.compressed(tile.uri ?? ''));
        // const texture = this.ensureTexturePowerOfTwo(image);
        const layer = new BackgroundSprite(image);
        layer.name = 'theme-background';
        layer.width = tile.width ?? 0;
        layer.height = tile.height ?? 0;
        layer.x = tile.x ?? 0;
        layer.y = tile.y ?? 0;
        layer.eventMode = 'none';
        layer.interactiveChildren = false;

        layers.push(layer);
      }
    } else if (this.isThemeContinuousBackground) {
      // assumes square grid
      const columns = Math.sqrt(tiles.length);
      // TODO: i do not understand why this is a constant, it should be social.startingWidth
      const pixiLayoutSize = 20000;
      const roomSize = social.startingWidth ?? 0;
      const width = pixiLayoutSize / (tiles.length / columns);
      const height = pixiLayoutSize / (tiles.length / columns);
      // cannot expect the tiles to be in order
      const sortedTiles = _.sortBy(tiles, ['y', 'x']);
      const tileLayouts = this.layoutTilesInGrid(sortedTiles, 4, pixiLayoutSize, pixiLayoutSize);
      for (const layout of tileLayouts) {
        // request size relative to actual room size
        const resizedURI = getResizeUri({ uri: layout.url ?? '', width: roomSize, height: roomSize });
        await Assets.load(this.compressed(resizedURI));
        const image = Texture.from(resizedURI);
        // const texture = this.ensureTexturePowerOfTwo(image);
        const tile = new BackgroundSprite(image);
        tile.width = width;
        tile.height = height;
        // convert to cartesian coordinates
        tile.position.x = layout.x - pixiLayoutSize / 2;
        tile.position.y = layout.y - pixiLayoutSize / 2;
        layers.push(tile);
      }
    } else {
      if (imageFarURI) {
        const t3 = await Assets.load(this.compressed(imageFarURI));
        // TODO: this is not a power of two
        // const texture = this.ensureTexturePowerOfTwo(t3);

        this.far = new ParallaxLayer(t3, textureWidth, textureHeight, 0.12, 0.03);
        this.far.name = 'far';
        parallaxLayers.push(this.far);
      }

      if (imageMidURI) {
        const t2 = await Assets.load(this.compressed(imageMidURI));
        this.mid = new ParallaxLayer(t2, textureWidth, textureHeight, 0.428, 0.1);
        this.mid.name = 'mid';
        parallaxLayers.push(this.mid);
      }

      if (imageNearURI) {
        const t1 = await Assets.load(this.compressed(imageNearURI));
        this.near = new ParallaxLayer(t1, textureWidth, textureHeight, 1.0, 0.5);
        this.near.name = 'near';
        parallaxLayers.push(this.near);
      }
    }

    // Add parallax layers to the viewport or stage
    // If continuous background set in viewport otherwise set to stage
    if (this.isThemeContinuousBackground) {
      let i = 0;
      for (const layer of layers) {
        if (tiles?.length) {
          this.viewport.moveCenter(0, 0);
          this.viewport.addChildAt(layer, i);
        } else {
          this.app!.stage.addChildAt(layer, i);
        }
        i++;
      }
    } else {
      parallaxLayers.forEach((layer, i) => {
        this.app!.stage.addChildAt(layer, i);
      });
    }

    this.backgroundSprites = this.isThemeContinuousBackground ? layers : parallaxLayers;
  }

  private compressed(uri: string) {
    return this.ktx2Compression ? uri?.replace('webp', 'ktx2') : uri;
  }

  private layoutTilesInGrid(
    images: SocialThemeImageTile[],
    gridSize: number,
    containerWidth: number,
    containerHeight: number,
  ): TileLayout[] {
    const imageSize = Math.min(containerWidth / gridSize, containerHeight / gridSize);

    const layouts: TileLayout[] = images.map((image, index) => {
      const row = Math.floor(index / gridSize);
      const col = index % gridSize;

      const x = col * imageSize;
      const y = row * imageSize;

      return {
        url: image.uri ?? '',
        x,
        y,
        width: imageSize,
        height: imageSize,
      };
    });

    return layouts;
  }

  private removeThemeBackground(): void {
    this.info('Removing theme background');
    if (this.isThemeContinuousBackground) {
      // Remove parallax layers from viewport for continuous background
      this.backgroundSprites?.forEach((sprite) => {
        this.viewport.removeChild(sprite);
        sprite.destroy({ children: true });
      });
      this.backgroundSprites = undefined;
      return;
    }
    if (this.far) {
      this.app!.stage.removeChild(this.far);
    }
    this.far?.destroy({ children: true });
    this.far = undefined;

    if (this.mid) {
      this.app!.stage.removeChild(this.mid);
    }
    this.mid?.destroy({ children: true });
    this.mid = undefined;

    if (this.near) {
      this.app!.stage.removeChild(this.near);
    }

    this.near?.destroy({ children: true });
    this.near = undefined;
  }

  public setParticipantVideoVisibility(id: string, visible: boolean): void {
    const player = this.participants.get(id);
    if (!player) {
      return;
    }

    if (!visible) {
      player.hideVideoSprite();
    }

    player.displayVideoSprite();
  }

  public getParticipant(id: string): Player | undefined {
    if (id === this.player?.userId) {
      return this.player;
    }
    return this.participants.get(id);
  }

  public getParticipants(ids: string[]): Player[] {
    return ids.map((pId) => this.getParticipant(pId)).filter(notNil);
  }

  public getPlayerAsync(userId?: string): Promise<Player> {
    const player = this.getPlayer(userId);

    const promise = new Promise<Player>((resolve) => {
      if (!player) {
        setTimeout(() => resolve(this.getPlayerAsync(userId)), 200);
      } else {
        resolve(player);
      }
    });
    return promise;
  }

  /**
   * Get player by userId.
   * @param userId The userID of the player to get. If not provided, the current player will be returned.
   * @returns The requested player or undefined.
   */
  public getPlayer(userId?: string): Player | undefined {
    if (!userId) {
      return this.player;
    }
    if (userId === this.player?.userId) {
      return this.player;
    }
    return this.participants.get(userId);
  }

  public getUser(userId: string): User | undefined {
    return this.getPlayer(userId)?.user;
  }

  public get participantsList(): Player[] {
    return [...this.participants.values()];
  }

  public get participantsAndPlayerList(): Player[] {
    return [this.player, ...this.participantsList].filter(notNil);
  }

  public hideScene(): void {
    this.hideParticipants();
    this.setWorldBorderVisibility(false);

    /**
     * customCursorVisible is required to avoid 'pointerover' event to
     * trigger 'customCursor.visible = true' - see scene-manager.ts -> setCustomCursor() -> .on('pointerover')
     * We'll always have to set customCursorVisible to false when we don't want the custom cursor to be visible
     * for an event.
     */
    this.setCustomCursorVisibility(false);
    this.hideCustomCursor();
  }

  public showScene(): void {
    this.showParticipants();
    this.reRenderCustomCursor();
    this.setCustomCursorVisibility(true);
    this.setWorldBorderVisibility(true);
    void delayFor(500).then(() => {
      this.healthCheckAll();
    });
  }

  /**
   * Ensures auto zoom is disabled - call this before any manual zoom action
   * Returns whether auto zoom was enabled before disabling
   */
  public ensureAutoZoomDisabled(): boolean {
    const wasEnabled = this.autoZoomEnabled;

    // Always cancel any pending auto zoom operations, even if auto zoom isn't enabled
    // This ensures we don't have any lingering zoom operations
    if (this.snapZoomTimeout) {
      clearTimeout(this.snapZoomTimeout);
      this.snapZoomTimeout = undefined;
      this.warn('Canceled pending auto zoom timeout');
    }

    // Cancel any in-progress animations using the controller
    this.zoomController.cancelZoomAnimations();
    this.warn('Canceled any in-progress zoom animations');

    if (wasEnabled) {
      // Force auto zoom off
      this.autoZoomEnabled = false;
      // Emit the new state to the observable
      this.autoZoomEnabled$.next(false);
      this.info('Auto zoom disabled by manual zoom action');
    }

    return wasEnabled;
  }

  public toggleAutoZoom(forceOff = false): boolean {
    this.autoZoomEnabled = forceOff ? false : !this.autoZoomEnabled;
    // Emit the new state to the observable
    this.autoZoomEnabled$.next(this.autoZoomEnabled);

    // Add debug logging to trace auto zoom state changes
    this.info(`Auto zoom ${this.autoZoomEnabled ? 'enabled' : 'disabled'}${forceOff ? ' (forced off)' : ''}`);

    // If auto zoom was just enabled, emit a zoom direction event to reset button states
    if (this.autoZoomEnabled) {
      this.info('Auto zoom enabled, emitting zoom direction to reset button states');
      this.zoomDirection$.next({ value: 'none' });
      this.updateAutoZoom();
    }

    return this.autoZoomEnabled;
  }

  /**
   * Start continuous zoom (when button is held down)
   * @param zoomIn Whether to zoom in (true) or out (false)
   */
  public startContinuousZoom(zoomIn: boolean) {
    // First, ensure we're not in the middle of an auto zoom transition
    // and immediately disable auto zoom
    const wasAutoZoomEnabled = this.ensureAutoZoomDisabled();

    // Cancel any ongoing zoom animations to prevent conflicts
    this.zoomController.cancelZoomAnimations();

    // If auto zoom was enabled, notify any interested components
    if (wasAutoZoomEnabled) {
      this.info(`Starting continuous zoom (${zoomIn ? 'in' : 'out'}), disabled auto zoom`);

      // Make sure the UI knows auto zoom is disabled
      this.autoZoomEnabled$.next(false);
    } else {
      this.info(`Starting continuous zoom (${zoomIn ? 'in' : 'out'})`);
    }

    // Start the actual continuous zoom using the controller
    this.zoomController.startContinuousZoom(zoomIn);

    // Setup a periodic cleanup during continuous zoom
    const cleanupInterval = setInterval(() => {
      this.zoomCleanup();
    }, 50);

    // Store the cleanup interval to clear it when zoom stops
    this.cleanupIntervalId = cleanupInterval;
  }

  /**
   * Stop continuous zoom
   */
  public stopContinuousZoom(): void {
    this.zoomController.stopContinuousZoom();

    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }

    this.zoomCleanup();
  }

  /**
   * Zoom to a specific level
   */
  public async zoomTo(zoomLevel: number, options?: ISnapZoomOptions): Promise<void> {
    await this.zoomController.zoomTo(zoomLevel, options);
    await this.zoomCleanup();
  }

  /**
   * Handle post-zoom adjustments
   */
  private zoomCleanup(): void {
    // Scale the cursor inversely to the zoom level to maintain apparent size
    this.customCursor.scale.set(1 / this.currentZoom);

    // Notify the group manager of zoom changes
    this.groupManager?.zoomChanged();

    // Update all player scales
    this.player?.adjustNameTagPositionAndScale(this.currentZoom);
    this.participants.forEach((p) => {
      p.adjustNameTagPositionAndScale(this.currentZoom);
    });

    // Notify UI components by emitting a zoom direction change to 'none'
    // This triggers bounds checking in the zoom controls component
    this.zoomDirection$.next({ value: 'none' });
  }

  private updateAutoZoom() {
    // If auto zoom is not enabled, do nothing
    if (!this.autoZoomEnabled) {
      this.info('Auto zoom calculation attempted but auto zoom is disabled - skipping');
      return;
    }

    const { groupId, x: playerX, y: playerY } = this.player ?? {};
    if (!groupId || !playerX || !playerY || !this.snapZoomOptions || !this.snapZoomOptions.ratio) {
      return;
    }

    const group = this.groupManager?.getGroup(groupId);
    let zoom = DEFAULT_ZOOM;

    if (group && group.membersArray.length > 1) {
      // Calculate zoom based on the furthest group member
      let furthestDistance = 0;
      group.membersArray.forEach((member) => {
        const { x, y } = member;
        // find distance between player and member and scale down
        const distance = Math.sqrt(Math.pow(playerX - x, 2) + Math.pow(playerY - y, 2)) / 1000;
        if (distance > furthestDistance) {
          furthestDistance = distance;
        }
      });

      zoom = -furthestDistance + this.snapZoomOptions.ratio;

      // Clamp zoom to min/max values
      zoom = Math.min(Math.max(zoom, this.zoomController.options.minZoom), this.maxZoom);
    }

    // Cancel any existing timeout to avoid rapid zooming
    if (this.snapZoomTimeout) {
      clearTimeout(this.snapZoomTimeout);
      this.snapZoomTimeout = undefined;
    }

    // Only proceed if auto zoom is still enabled (it could have been disabled during calculation)
    if (!this.autoZoomEnabled) {
      this.info('Auto zoom was disabled during calculation, canceling auto zoom update');
      return;
    }

    this.info(`Scheduling auto zoom to ${zoom} in 500ms`);
    this.snapZoomTimeout = window.setTimeout(() => {
      // Double-check that auto zoom is still enabled before applying
      if (this.autoZoomEnabled) {
        this.info(`Applying auto zoom to ${zoom}`);

        // Signal that we're about to perform auto zoom - this will help reset button states
        this.zoomDirection$.next({ value: 'none' });

        void this.zoomTo(zoom, {
          ...this.snapZoomOptions,
          time: this.snapZoomOptions.time,
        });
      } else {
        this.info('Auto zoom was disabled before timeout completed, canceling auto zoom');
      }
    }, 500);
  }

  private updateSpeaker({ deviceId }: SimpleMediaDeviceInfo): void {
    this.participants.forEach(async (p) => {
      try {
        const audioElement = p.audioElement;
        if (audioElement) {
          await audioElement.setSinkId?.(deviceId);
        }
      } catch (error) {
        this.error('updateSpeaker error', error);
      }
    });
  }

  public hideCustomCursor(): void {
    this.app!.stage.cursor = CursorTypes.PREDETERMINED;
    this.customCursor.visible = false;
    this.setCustomCursorVisibility(false);
    if (this.app!.view && this.app!.view.style) {
      this.app!.view.style.cursor = 'default';
    }
  }

  public showCustomCursor() {
    if (this.app && this.app.view && this.app.view.style) {
      this.app.view.style.cursor = 'none';
    }
    this.setCustomCursorVisibility(true);
  }

  public reRenderCustomCursor(): void {
    // This is a "hack" to show custom cursor, we need to set the default cursor which is 'none' in order to re render
    // the custom cursor due to pixi limitations. See setCustomCursor().
    // this.app.stage.cursor = this.socialMouseMovementEnabled ? 'default' : CursorTypes.PREDETERMINED;
    this.app!.stage.cursor = 'default';
    this.customCursor.visible = true;
  }

  public addParticipant(player: Player): void {
    this.participants.set(player.userId, player);
    player.zIndex = ZIndex.Player;
    this.roomContainer.addChild(player);
    this.playerMap[player.userId] = player;
    this.participantsCountChange.next(this.participants.size);

    const prev = this.preParticipantMap[player.userId];
    if (prev) {
      const limitExceeded = notNil(prev.w) ? prev.w : false;
      this.groupManager?.setGroup({
        clusterId: prev.c,
        player,
        limitExceeded,
        sag: prev.sag,
      });
    }
    if (this.centers) {
      this.setGroupCenters(this.centers);
    }
    this.participantAdded$.next(player);
  }

  public removeAllParticipants(): void {
    for (const p of this.participants.values()) {
      this.removeParticipant(p.userId);
    }

    this.participants.clear();
    this.preParticipantMap = {};
  }

  public removeParticipant(id: string): void {
    const participant = this.participants.get(id);
    if (!participant) {
      return;
    }
    // Remove from internal maps
    this.participants.delete(id);
    delete this.playerMap[participant.userId];

    // Remove from group if any
    this.groupManager?.removeParticipant(participant);

    // Remove from roomContainer and destroy player
    this.roomContainer.removeChild(participant);
    participant.destroy({ children: true });

    // emit that the participant count changed
    this.participantsCountChange.next(this.participants.size);
    this.participantRemoved$.next(id);
  }

  public removePlayer(): void {
    if (this.player) {
      this.roomContainer.removeChild(this.player);
    }

    this.player?.destroy({ children: true });
    this.player = undefined;
  }

  public addPlayer(p: Player): void {
    this.player = p;
    this.playerMap[p.userId] = p;
    p.zIndex = ZIndex.Player;
    this.participantAdded$.next(p);
  }

  public hideParticipants(): void {
    this.roomContainer.visible = false;
  }

  public showParticipants(): void {
    this.roomContainer.visible = true;
  }

  public setWorldBorderVisibility(visible: boolean): void {
    if (this.border) {
      this.border.visible = visible;
    }
  }

  private async setInitialPosition({
    cluster,
    clusterX,
    clusterY,
    sag,
    waiting,
    x,
    y,
  }: InitialPositionMessage): Promise<void> {
    const player = await this.getPlayerAsync();
    player.x = x;
    player.y = y;

    this.roomContainer.addChild(player);
    this.viewport.moveCenter(x, y);

    this.bounds = this.viewport.getVisibleBounds();

    // If we had initial positions, disregard. The initial position message provides all necessary position
    // and group data.
    delete this.preParticipantMap[player.userId];

    this.groupManager?.destroy();

    this.groupManager = new GroupManager(this.sceneService, this.store, this._snackBar);
    this.groupManager.setGroup({
      clusterId: cluster,
      player,
      limitExceeded: waiting,
      center: { x: clusterX, y: clusterY },
      sag,
    });

    void this.executeZoomSequence();
  }

  public async executeZoomSequence(): Promise<void> {
    this.snapZoomOptions.time = 1000;
    await delayFor(3500);

    this.snapZoomOptions.width = this.w;
    await this.zoomTo(this.zoomController.options.minZoom, this.snapZoomOptions);
    await delayFor(2000);

    this.snapZoomOptions.width = window.innerWidth;
    // zooming out too far in small rooms breaks the viewport, check minZoom
    await this.zoomTo(Math.max(DEFAULT_ZOOM, this.zoomController.options.minZoom), this.snapZoomOptions);
    await delayFor(this.snapZoomOptions.time);

    this.setCustomCursorVisibility(true);

    const socialMiniMap = SocialMiniMapComponent.getInstance();
    if (socialMiniMap) {
      socialMiniMap.setVisibilityToDefault();
    }
  }

  private setPositions(positions: ParticipantPosition[] | ParticipantMovement[], isInitial = false): void {
    if (this.playerCulling) {
      this.removeInvisiblePlayers();
    }

    if (!isInitial && !this.isLoading) {
      this.healthCheckAll();
    }

    positions.forEach(async (position) => {
      // This shouldn't be necessary when we standardize the ParticipantPosition and ParticipantMovement
      const positionUserId = (position as ParticipantPosition).userId;
      const movementUserId = (position as ParticipantMovement).u;
      const sag = (position as ParticipantMovement).sag;
      const teleported = (position as ParticipantMovement).teleported;
      const userId = positionUserId || movementUserId;
      const { c: clusterId, w: waiting, x, y } = position;

      const { groupId } = this.player ?? {};

      if (userId === this.player?.userId && waiting) {
        this.sceneService.displayMaxGroupBanner();
      } else if (userId === this.player?.userId && !waiting && !this.fullClusters.includes(groupId ?? -1)) {
        this.sceneService.removeMaxGroupBanner();
      }

      const player = this.playerMap[userId];
      if (!player) {
        this.preParticipantMap[userId] = { x, y, c: clusterId, w: waiting };
        return;
      }

      this.healthCheck(player);

      const limitExceeded = notNil(waiting) ? waiting : false;
      if (clusterId) {
        this.groupManager?.setGroup({ clusterId, player: player, limitExceeded, sag });
      }

      if (teleported) {
        player.teleport(x, y);
        if (player.isPrimaryPlayer) {
          this.viewport.moveCenter(x, y);
        }
        // TODO: is timeout necessary?
        await delayFor(100);
        this.updateAllParticipantAudio();
        return;
      }

      player.setTargetPosition(x, y);
    });
  }

  // TODO: refactor into soundControl
  public updateAllParticipantAudio(): void {
    if (!this.player) {
      return;
    }
    const mainPlayer = this.player;
    for (const p of this.participants.values()) {
      const volume = this.soundControl.getPlayerVolumeByDistance({
        enableAudioRange: this.enableAudioRange,
        mainPlayer,
        p,
      });

      this.soundControl.setParticipantVolume({
        p,
        volume,
        setBy: 'teleport audio update',
      });
    }
  }

  private removeInvisiblePlayers(): void {
    this.playerVisibilityService.processInvisiblePlayers({
      participants: this.participants,
      viewport: this.viewport,
      healthCheckFn: this.healthCheck.bind(this),
    });
  }

  public healthCheckAll(): void {
    this.playerVisibilityService.healthCheckAll({
      player: this.player,
      participants: this.participants,
      healthCheckFn: this.healthCheck.bind(this),
    });
  }

  public healthCheck(player: Player): void {
    if (this.healthCheckFlag) {
      if (!player.parent && player.visible) {
        this.info(`Re-adding culled player ${player.userId} to room container`);
        this.roomContainer.addChild(player);
        player.zIndex = ZIndex.Player;
      }
      player.healthCheck();
    }

    // Handle the main player separately to ensure it's always correctly positioned
    if (this.player && player.userId === this.player.userId) {
      const roomContainer = this.roomContainer;
      const playerSprite = roomContainer.getChildByName(this.player.name ?? '');
      if (playerSprite && playerSprite.x !== player.x) {
        this.error('Player sprite and player position are out of sync, healing...');
        roomContainer.removeChild(playerSprite);
        roomContainer.addChild(this.player);
      }
    }

    // Ensure player has the correct visibility state
    if (player.visible && !player.wrapperContainer) {
      this.info(`Forcing rebuild for player ${player.userId} - visible but missing wrapper`);
      // Force a rebuild if the player is visible but has no wrapper
      void player.createPlayer();
    }
  }

  /**
   * Extract background visibility check to a separate method for better organization
   * This optimizes how we check and update background sprite visibility
   */
  private updateBackgroundVisibility(): void {
    if (!this.backgroundSprites || !this.bounds) {
      return;
    }

    try {
      // Process each sprite to check if it's in view
      this.backgroundSprites.forEach((sprite) => {
        if (!sprite) {
          return;
        }

        // Fast rectangle intersection test
        sprite.visible =
          sprite.x + sprite.width > this.bounds.left &&
          sprite.x < this.bounds.right &&
          sprite.y + sprite.height > this.bounds.top &&
          sprite.y < this.bounds.bottom;
      });
    } catch (e) {
      this.error('Error in updateBackgroundVisibility', e);
    }
  }

  private setGroupCenters(centers: ClusterCenter[]): void {
    this.centers = centers;
    centers.forEach((center) => {
      const { c: groupId } = center;

      this.groupManager?.setGroupCenter(center);

      // check for autozoom and update zoom if necessary
      if (this.autoZoomEnabled && this.groupManager && !this.player?.isMoving) {
        _.debounce(() => {
          void this.updateAutoZoom();
        }, this.snapZoomOptions.delay)();
      }
      // We check if the group id is already in the fullClusters array
      // we don't want to dispatch an action every time a user moves.
      if (center.overfull && !this.fullClusters.includes(groupId)) {
        this.store.dispatch(actions.ADD_FULL_CLUSTER({ groupId }));
      } else if (!center.overfull && this.fullClusters.includes(groupId)) {
        this.store.dispatch(actions.REMOVE_FULL_CLUSTER({ groupId }));
      }
    });
  }

  public runPixiMainLoop(): void {
    //Parallax effect
    if (this.viewport && this.near && this.far && this.mid) {
      this.far.viewportX = -this.viewport.x;
      this.far.viewportY = -this.viewport.y;

      this.mid.viewportX = -this.viewport.x;
      this.mid.viewportY = -this.viewport.y;

      this.near.viewportX = -this.viewport.x;
      this.near.viewportY = -this.viewport.y;
    }

    if (this.viewport) {
      this.bounds = this.viewport.getVisibleBounds();
      // When using continuous backgrounds, hide background sprites that are not visible
      if (this.isThemeContinuousBackground) {
        this.backgroundSprites?.forEach((sprite) => {
          try {
            sprite.visible =
              sprite.x + sprite.width > this.bounds.left &&
              sprite.x < this.bounds.right &&
              sprite.y + sprite.height > this.bounds.top &&
              sprite.y < this.bounds.bottom;
          } catch (e) {
            sprite.visible = false;
            this.error('Error in runPixiMainLoop', e);
          }
        });
      }

      if (this.player) {
        this.player.renderEcho();
        this.groupManager?.moveGroupCenter(this.viewport, this.player);
        this.movePlayers();
      }
    }

    for (const p of this.participantsList) {
      if (p.sound) {
        p.renderEcho();
      }
    }
  }

  public async destroy(): Promise<void> {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = undefined;
    this.groupManager?.destroy();

    // Remove all event listeners
    this.mouseMovementController?.viewport
      ?.off('pointermove', this.handlePointerMove)
      .off('pointerdown', this.handlePointerDown)
      .off('pointerover', this.handlePointerOver)
      .off('pointerout', this.handlePointerOut)
      .off('pointerup', this.onMouseLeave)
      .off('mouseup', this.onPointerUp);

    this.viewport?.off('pinch-start', this.handlePinch);

    document.documentElement.removeEventListener('onmouseleave', this.mouseMovementController.onMouseLeave);

    this.destroyArtifacts();
    this.destroyCTAItems();
    this.destroyNavItems();

    const tileUris = this.social?.theme?.tiles?.map((tile) => this.compressed(tile.uri ?? ''));
    if (tileUris) {
      await Assets.unload(tileUris);
    }
    this.viewport.removeChild(this.roomContainer);

    if (this.app) {
      try {
        this.removeAllParticipants();
        this.app.stop();
        this.app = undefined;
      } catch (e) {
        this.error('Error trying to shutdown Pixi', e);
      }
    }
    this.joystick?.destroy();
    this.mouseMovementController.destroy();
    document.documentElement.onmouseleave = null;
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.social = undefined;

    Assets.reset();
  }

  /**
   * Changes the cursor angle based on mouse and player positions.
   * @param mousePosition Mouse position relative to the world.
   */
  private changeCursorAngle(mousePosition?: Partial<Rectangle>): void {
    if (mousePosition) {
      this.lastMousePos = mousePosition;
    }
    if (!this.lastMousePos) {
      return;
    }

    const { x, y } = this.lastMousePos;

    // Event data returns position of the cursor relative to the screen, we need the absolute
    // position in order to compare it against user.
    if (x === undefined || y === undefined) {
      return;
    }
    const cursorAbsolutePosition = this.mouseMovementController.getCursorAbsolutePosition(x, y);

    // FIXME: This line fixes position but makes hit detection unstable.
    // this.customCursor.anchor.set(0.5, 0.5);
    this.customCursor.position.set(cursorAbsolutePosition.x, cursorAbsolutePosition.y);

    if (!this.player || !this.player.cursorIndicator) {
      return;
    }
    const cursorAngle = this.mouseMovementController.getCursorAngle({
      player: this.player,
      cursorAbsolutePosition,
    });
    this.customCursor.angle = cursorAngle;
    this.player.cursorIndicator.angle = cursorAngle;
  }

  private updateCameraStream(stream: MediaStream | undefined) {
    this.info('Received new camera stream', stream?.id);

    if (!this.player) {
      this.trace('Received camera stream when player was not set');
      return;
    }

    if (!stream) {
      this.player.enableVideo(false);
      return;
    }

    this.player.hideVideoSprite();

    const videoTracks = stream.getVideoTracks();
    videoTracks[0].enabled = this.isVideoEnabled;

    this.player.setVideoElementSource(stream);

    // this.player.displayVideoSprite();
    this.player.enableVideo(this.isVideoEnabled);
  }

  private async updateEchoAudioStream(stream: MediaStream): Promise<void> {
    this.info('Received new echo audio stream', stream.id);

    if (!this.player) {
      this.trace('Received echo audio stream when player was not set');
      return;
    }

    const { sound } = this.player;
    const audioContext = this.soundControl.audioContext;
    await audioContext?.resume();

    if (!sound) {
      return;
    }

    // Disconnect any existing audio source.
    sound.audioSource?.disconnect(sound.analyser);

    // Create a new audio source for the passed stream, and connect it to the analyser.
    sound.audioSource = audioContext?.createMediaStreamSource(stream);
    sound.audioSource?.connect(sound.analyser);
    this.player.setSound(sound);
    this.player.renderEcho();
  }

  private destroyArtifacts(): void {
    this.artifacts.map((a) => {
      this.roomContainer.removeChild(a);
      a.destroy();
    });
    this.artifacts = [];
  }

  private destroyCTAItems(): void {
    this.ctaItems.map((item) => {
      if (item.buttonSprite) {
        this.roomContainer.removeChild(item.buttonSprite);
      }
      if (item.imageSprite) {
        this.roomContainer.removeChild(item.imageSprite);
      }

      item.destroy();
    });
    this.ctaItems = [];
  }

  private destroyNavItems(): void {
    this.navItems.map((a) => {
      this.roomContainer.removeChild(a);
      a.destroy();
    });
  }

  /**
   * Clear existing items from the scene
   */
  public clear(): void {
    this.removeAllParticipants();
    this.removePlayer();
    this.groupManager?.removeAllGroups();
  }

  public setPlayerConnectionStatus(id: string, status: ConnectionStatus): void {
    const player = this.participants.get(id);
    if (!player) {
      return;
    }
    player.setConnectionStatus(status);
  }

  public updateRingColors(userId: string, ringColor: string) {
    const player = this.getPlayer(userId);
    if (!player || player.ringColor === ringColor) {
      return;
    }

    player.updateRingColor(ringColor);
    const socialMiniMap = SocialMiniMapComponent.getInstance();

    if (!socialMiniMap) {
      return;
    }

    const miniMapUser = socialMiniMap.participants.get(userId);

    if (!miniMapUser) {
      return;
    }

    miniMapUser.setDotGraphic(ringColor);
  }

  private get isThemeContinuousBackground(): boolean {
    return (this.social?.theme?.tiles?.length ?? 0) > 0;
  }

  // Ensure that the texture is a power of two, Pow2 textures are required for mipmapping and are more efficient
  public ensureTexturePowerOfTwo(texture: Texture): Texture {
    // const { height, width } = texture;

    // // Calculate the next power-of-two dimensions
    // const newWidth = nextPow2(width);
    // const newHeight = nextPow2(height);

    // // If already power-of-two, return the original texture
    // if (width === newWidth && height === newHeight) {
    //   return texture;
    // }

    // // Create a RenderTexture and render the original texture onto it
    // const renderTexture = RenderTexture.create({ width: newWidth, height: newHeight });
    // const sprite = new Sprite(texture);
    // sprite.width = newWidth;
    // sprite.height = newHeight;
    // this.app!.renderer.render(sprite, { renderTexture });

    // return new Texture(renderTexture);
    return texture;
  }

  private async handleSpecialAccessGroup(msg: MovedMessage) {
    const { c: clusters, p: participants } = msg;

    for (const cluster of clusters) {
      const { c: groupId, sag, x, y } = cluster;
      this.sagClusters.set(groupId, { x, y, sag: sag ?? false });
    }

    const localPlayer = await this.getPlayerAsync();

    for (const participant of participants) {
      const { c: groupId, d: denied, r: requestingAccess, sag, u: userId } = participant;
      const player = this.getParticipant(userId) ?? (await this.getPlayerAsync(userId));
      if (!player) {
        return;
      }
      // Process SAG states
      if (sag) {
        // Show local player notification if they are in the group
        const isPlayerInQueue = this.sagQueue.get(groupId)?.some((user) => user.userId === localPlayer.userId);

        if (!requestingAccess && localPlayer.userId === userId && !localPlayer.inSag && isPlayerInQueue) {
          this.store.dispatch(
            actions.UPDATE_SAG_MODAL({
              mode: SpecialAccessGroupModalMode.REQUEST_ACCESS,
              open: false,
            }),
          );
          this.sceneService.showNotification('success', "You're in!", 'Welcome to the group!', 'check');
        }
        // Participant is in SAG group
        player.inSag = true;
        player.isGroupSag = true;

        // set sound
        const volume = this.soundControl.getPlayerVolumeByDistance({
          mainPlayer: localPlayer,
          p: player,
          enableAudioRange: this.enableAudioRange,
        });
        this.soundControl.setParticipantVolume({ p: player, volume, setBy: 'sag' });

        player.toggleGroup({
          warning: false,
          groupId,
          isInGroup: player.playerGroupInfo?.isInGroup ?? false,
          isPrimaryGroup: player.playerGroupInfo?.isPrimaryGroup ?? false,
        });

        const currQueue = this.sagQueue.get(groupId) ?? [];
        this.sagQueue.set(
          groupId,
          currQueue?.filter((user) => user.userId !== player.userId),
        );
        this.sagQueue$.next(this.sagQueue);
      } else if (requestingAccess) {
        // Participant is requesting access
        player.isGroupSag = true;
        player.inSag = false;
        const currQueue = this.sagQueue.get(groupId) ?? [];
        if (!currQueue.some((user) => user.userId === player.userId)) {
          this.sagQueue.set(groupId, [...currQueue, player.user]);
          this.sagQueue$.next(this.sagQueue);
        }
      } else if (denied) {
        // Access denied
        player.isGroupSag = true;
        player.inSag = false;
        player.toggleGroup({
          warning: true,
          groupId,
          isInGroup: false,
          isPrimaryGroup: false,
        });

        // If it's the current player, close modal and show notification
        if (localPlayer?.userId === userId) {
          this.store.dispatch(
            actions.UPDATE_SAG_MODAL({
              mode: SpecialAccessGroupModalMode.REQUEST_ACCESS,
              open: false,
            }),
          );
          this.sceneService.showNotification('error', 'Access Denied', 'You cannot join this group', 'highlight_off');
        }
      }

      // Find the corresponding cluster
      const cluster = this.sagClusters.get(groupId);

      if (!cluster) {
        return;
      }

      // If cluster is not SAG, remove any SAG state from player
      if (!cluster.sag) {
        if (player.inSag || player.isGroupSag) {
          player.inSag = false;
          player.isGroupSag = false;
          player.toggleGroup({
            warning: false,
            groupId,
            isInGroup: player.playerGroupInfo?.isInGroup ?? false,
            isPrimaryGroup: player.playerGroupInfo?.isPrimaryGroup ?? false,
          });
        }
        this.store.dispatch(
          actions.UPDATE_SAG_MODAL({
            mode: SpecialAccessGroupModalMode.REQUEST_ACCESS,
            open: false,
          }),
        );

        for (const [queueGroupId, users] of this.sagQueue) {
          const updatedUsers = users.filter((user) => user.userId !== player.userId);
          if (updatedUsers.length !== users.length) {
            if (updatedUsers.length === 0) {
              this.sagQueue.delete(queueGroupId);
            } else {
              this.sagQueue.set(queueGroupId, updatedUsers);
            }
          }
          this.sagQueue$.next(this.sagQueue);
        }
        return;
      }

      if (cluster.sag && player.inSag && !sag) {
        player.inSag = false;
        player.isGroupSag = true;
        player.toggleGroup({
          warning: false,
          groupId,
          isInGroup: player.playerGroupInfo?.isInGroup ?? false,
          isPrimaryGroup: player.playerGroupInfo?.isPrimaryGroup ?? false,
        });
      }

      if (cluster.sag && !player.inSag) {
        player.inSag = false;
        player.isGroupSag = true;
        player.toggleGroup({
          warning: true,
          groupId,
          isInGroup: player.playerGroupInfo?.isInGroup ?? false,
          isPrimaryGroup: player.playerGroupInfo?.isPrimaryGroup ?? false,
        });

        if (localPlayer.userId === userId) {
          this.store.dispatch(
            actions.UPDATE_SAG_MODAL({
              mode: SpecialAccessGroupModalMode.REQUEST_ACCESS,
              open: true,
            }),
          );
        }
      }
    }
  }

  private handleSagDebuggerData(msg: MovedMessage) {
    const { c: clusters, p: players } = msg;
    const participant = this.participantsAndPlayerList.find((item) => item.userId === players[0].u);
    if (!participant) {
      return;
    }

    participant.playerSagInfo = {
      p: players[0] ?? participant.playerSagInfo?.p,
      c: clusters[0] ?? participant.playerSagInfo?.c,
    };
  }
}
// ZoomDirection is now exported from zoom-controller.ts

export interface ExtendedISnapZoomOptions extends ISnapZoomOptions {
  ratio?: number;
  delay?: number;
}

interface TileLayout {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface sagMapMap {
  admitted: Map<string, User>;
  queue: Map<string, User>;
}
